import { Checkbox } from 'antd';
import cn from 'classnames';
import {
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useWindowSize } from '@app/hooks';

import {
  useLeadsRegistryColumns,
  useLeadsRegistryFilters,
  useLeadsRegistryScrollRestore,
  useLeadsRegistrySelection,
} from '../../../../hooks';
import { buildLeadsRegistrySearchFilter } from '../../../../model/adapters/buildLeadsRegistrySearchFilter';
import { buildLeadsRegistrySearchSignature } from '../../../../model/adapters/buildLeadsRegistrySearchSignature';
import {
  LEADS_REGISTRY_ROW_HEIGHT,
  LEADS_REGISTRY_TABLE_DESKTOP_SCROLL_OFFSET,
  LEADS_REGISTRY_TABLE_MIN_SCROLL_Y,
  LEADS_REGISTRY_TABLE_MOBILE_SCROLL_OFFSET,
  LEADS_SCROLL_LOAD_THRESHOLD_PX,
} from '../../../../model/constants';
import { useLeadsRegistryVirtualRows } from '../hooks/useLeadsRegistryVirtualRows';

import type { LeadsRegistryTableRow } from '../../../../model/types';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type { ColumnType } from 'antd/es/table';
import type { CSSProperties, FC, ReactNode, UIEvent } from 'react';

import './LeadsRegistryTable.scss';

const SELECTION_COLUMN_WIDTH = 42;
// Renders extra rows above/below the viewport so fast flings have a buffer
// before the rendered band scrolls out of view; the static placeholder layer
// covers anything beyond it.
const LEADS_TABLE_OVERSCAN = 10;
const FALLBACK_COLUMN_WIDTH = 160;
// Width of the client column in compact (scrolled-right) mode.
const COMPACT_CLIENT_COLUMN_WIDTH = 86;
const CLIENT_COLUMN_KEY = 'client';

export interface LeadsRegistryTableProps {
  hasMoreLeads: boolean;
  isLoadingMore: boolean;
  rows: LeadsRegistryTableRow[];
  onLoadMore(): void;
}

const getRowClassName = (
  row: LeadsRegistryTableRow,
  highlightedLeadId: number | null
) => {
  if (row.kind === 'skeleton') {
    return 'LeadsRegistryTable__row LeadsRegistryTable__row--skeleton';
  }

  // Голубой фон строки означает «неотвечено». Модификатор исторически назван
  // --unread, переименование — отдельной задачей.
  const baseClassName = row.leadInfo.isNoAnswer
    ? 'LeadsRegistryTable__row LeadsRegistryTable__row--unread'
    : 'LeadsRegistryTable__row';

  // Строка, из которой вернулись: подсвечивается на пару секунд, чтобы глаз
  // сразу нашёл место, откуда ушёл.
  return row.id === highlightedLeadId
    ? `${baseClassName} LeadsRegistryTable__row--restored`
    : baseClassName;
};

const getRowKey = (row: LeadsRegistryTableRow) =>
  row.kind === 'lead' ? row.id : row.skeletonId;

const getColumnWidth = (column: ColumnType<LeadsRegistryTableRow>) => {
  if (typeof column.width === 'number') {
    return column.width;
  }

  if (typeof column.width === 'string') {
    const parsedWidth = Number.parseInt(column.width, 10);

    return Number.isNaN(parsedWidth) ? FALLBACK_COLUMN_WIDTH : parsedWidth;
  }

  return FALLBACK_COLUMN_WIDTH;
};

const getTableWidth = (columnWidths: number[]) =>
  columnWidths.reduce((total, width) => total + width, SELECTION_COLUMN_WIDTH);

const getColumnKey = (
  column: ColumnType<LeadsRegistryTableRow>,
  index: number
) => String(column.key ?? column.dataIndex ?? index);

const renderColumnTitle = (column: ColumnType<LeadsRegistryTableRow>) => {
  if (typeof column.title === 'function') {
    return null;
  }

  return column.title;
};

interface RenderedCellLike {
  children?: ReactNode;
}

const isRenderedCellLike = (
  value: ReactNode | RenderedCellLike
): value is RenderedCellLike =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !isValidElement(value) &&
  'children' in value;

const renderColumnCell = (
  column: ColumnType<LeadsRegistryTableRow>,
  row: LeadsRegistryTableRow,
  rowIndex: number
) => {
  const rendered = column.render
    ? column.render(undefined, row, rowIndex)
    : null;

  return isRenderedCellLike(rendered) ? rendered.children : rendered;
};

export const LeadsRegistryTable: FC<LeadsRegistryTableProps> = ({
  hasMoreLeads,
  isLoadingMore,
  onLoadMore,
  rows,
}) => {
  const { innerHeight, isMobile } = useWindowSize();
  const { filters } = useLeadsRegistryFilters();
  // Подпись фильтров: при других фильтрах список другой, и сохранённое место
  // к нему уже не относится.
  const searchSignature = useMemo(
    () =>
      buildLeadsRegistrySearchSignature(
        buildLeadsRegistrySearchFilter(filters)
      ),
    [filters]
  );
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { rememberLeadPosition, highlightedLeadId } =
    useLeadsRegistryScrollRestore({
      rows,
      scrollContainerRef,
      searchSignature,
    });
  const columns = useLeadsRegistryColumns({ onLeadOpen: rememberLeadPosition });
  const { selectedLeadIds, setSelectedLeadIds } = useLeadsRegistrySelection();
  const [isScrolledRight, setIsScrolledRight] = useState(false);
  const [scrollViewportWidth, setScrollViewportWidth] = useState(0);
  const scrollY = useMemo(() => {
    const offset = isMobile
      ? LEADS_REGISTRY_TABLE_MOBILE_SCROLL_OFFSET
      : LEADS_REGISTRY_TABLE_DESKTOP_SCROLL_OFFSET;

    return Math.max(LEADS_REGISTRY_TABLE_MIN_SCROLL_Y, innerHeight - offset);
  }, [innerHeight, isMobile]);

  const tableColumns = useMemo(
    () =>
      columns.filter(
        (column): column is ColumnType<LeadsRegistryTableRow> =>
          !('children' in column)
      ),
    [columns]
  );

  const fullColumnWidths = useMemo(
    () => tableColumns.map(getColumnWidth),
    [tableColumns]
  );

  const compactColumnWidths = useMemo(
    () =>
      tableColumns.map((col, index) =>
        col.key === CLIENT_COLUMN_KEY
          ? COMPACT_CLIENT_COLUMN_WIDTH
          : fullColumnWidths[index]
      ),
    [tableColumns, fullColumnWidths]
  );

  const fullTableWidth = useMemo(
    () => getTableWidth(fullColumnWidths),
    [fullColumnWidths]
  );

  const compactTableWidth = useMemo(
    () => getTableWidth(compactColumnWidths),
    [compactColumnWidths]
  );

  // Shrinking the client column takes a few hundred pixels off the table. On
  // viewports where the narrowed table would fit, the horizontal scrollbar
  // disappears, the browser resets scrollLeft to 0, the column expands, the
  // scrollbar returns - the column visibly jitters. So compact only while the
  // narrowed table still overflows: the scroll offset compact mode depends on
  // then cannot vanish as a result of entering compact mode.
  const isCompact = isScrolledRight && compactTableWidth > scrollViewportWidth;

  const columnWidths = isCompact ? compactColumnWidths : fullColumnWidths;
  const tableWidth = isCompact ? compactTableWidth : fullTableWidth;

  // Контейнер шире суммы колонок — свободное место раздаётся колонкам
  // пропорционально их ширинам (task/11137): иначе таблица оставалась в своей
  // фиксированной ширине и справа от последней колонки зияла пустота. Пока
  // ширина вьюпорта ещё не измерена (0), растягивание не включаем.
  const shouldStretchColumns =
    scrollViewportWidth > 0 && tableWidth < scrollViewportWidth;

  const gridTemplateColumns = useMemo(
    () =>
      `${SELECTION_COLUMN_WIDTH}px ${columnWidths
        .map((width) =>
          // minmax(Wpx, Wfr): колонка не уже своей базовой ширины, а доля fr
          // пропорциональна ей — пропорции ширин сохраняются.
          shouldStretchColumns ? `minmax(${width}px, ${width}fr)` : `${width}px`
        )
        .join(' ')}`,
    [columnWidths, shouldStretchColumns]
  );

  const currentLeadIds = useMemo(() => {
    const leadIds: number[] = [];

    rows.forEach((row) => {
      if (row.kind === 'lead') {
        leadIds.push(row.id);
      }
    });

    return leadIds;
  }, [rows]);

  const selectedLeadIdsSet = useMemo(
    () => new Set<number>(selectedLeadIds),
    [selectedLeadIds]
  );

  const isAllCurrentRowsSelected =
    currentLeadIds.length > 0 &&
    currentLeadIds.every((leadId) => selectedLeadIdsSet.has(leadId));
  const isSomeCurrentRowsSelected =
    currentLeadIds.some((leadId) => selectedLeadIdsSet.has(leadId)) &&
    !isAllCurrentRowsSelected;

  const virtualViewportHeight = scrollY;
  const [scrollViewportHeight, setScrollViewportHeight] = useState(
    virtualViewportHeight
  );
  const { range, updateScrollTop, visibleRows } = useLeadsRegistryVirtualRows({
    overscan: LEADS_TABLE_OVERSCAN,
    rowHeight: LEADS_REGISTRY_ROW_HEIGHT,
    rows,
    viewportHeight: scrollViewportHeight,
  });

  const lastLoadMoreRowsCountRef = useRef<number | null>(null);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer) {
      return;
    }

    const updateViewportSize = () => {
      setScrollViewportHeight((prev) => {
        const next = scrollContainer.clientHeight;

        return prev === next ? prev : next;
      });
      // clientWidth excludes the vertical scrollbar, so it is the width the
      // table content actually has to fit into when deciding on compact mode.
      setScrollViewportWidth((prev) => {
        const next = scrollContainer.clientWidth;

        return prev === next ? prev : next;
      });
    };

    updateViewportSize();

    const resizeObserver = new ResizeObserver(updateViewportSize);
    resizeObserver.observe(scrollContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (isLoadingMore) return;

    lastLoadMoreRowsCountRef.current = null;
  }, [isLoadingMore, rows.length]);

  const selectAllChangeHandler = useCallback(
    ({ target: { checked } }: CheckboxChangeEvent) => {
      if (checked) {
        setSelectedLeadIds(
          Array.from(new Set<number>([...selectedLeadIds, ...currentLeadIds]))
        );
        return;
      }

      const currentLeadIdsSet = new Set<number>(currentLeadIds);
      setSelectedLeadIds(
        selectedLeadIds.filter((leadId) => !currentLeadIdsSet.has(leadId))
      );
    },
    [currentLeadIds, selectedLeadIds, setSelectedLeadIds]
  );

  const rowSelectionChangeHandler = useCallback(
    (leadId: number, checked: boolean) => {
      if (checked) {
        setSelectedLeadIds(
          Array.from(new Set<number>([...selectedLeadIds, leadId]))
        );
        return;
      }

      setSelectedLeadIds(selectedLeadIds.filter((id) => id !== leadId));
    },
    [selectedLeadIds, setSelectedLeadIds]
  );

  const scrollHandler = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const { clientHeight, scrollHeight, scrollTop, scrollLeft } =
        event.currentTarget;
      updateScrollTop(scrollTop);
      setIsScrolledRight(scrollLeft > 0);

      if (isLoadingMore || !hasMoreLeads) {
        return;
      }

      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      if (
        distanceToBottom <= LEADS_SCROLL_LOAD_THRESHOLD_PX &&
        lastLoadMoreRowsCountRef.current !== rows.length
      ) {
        lastLoadMoreRowsCountRef.current = rows.length;
        onLoadMore();
      }
    },
    [hasMoreLeads, isLoadingMore, onLoadMore, rows.length, updateScrollTop]
  );

  const tableStyle = useMemo<CSSProperties>(
    () => ({
      minWidth: tableWidth,
      // При растягивании таблица занимает всю ширину скролл-контейнера, доли fr
      // грида раздают её колонкам (task/11137).
      width: shouldStretchColumns ? '100%' : tableWidth,
    }),
    [shouldStretchColumns, tableWidth]
  );

  const gridStyle = useMemo<CSSProperties>(
    () => ({
      gridTemplateColumns,
    }),
    [gridTemplateColumns]
  );

  const spacerStyle = useMemo<CSSProperties>(
    () => ({
      height: range.totalHeight,
    }),
    [range.totalHeight]
  );

  // A spacer div pushes __visible-rows to the correct virtual position while
  // keeping it in the normal document flow. position:sticky works only in
  // flow-positioned elements; position:absolute ancestors break it because the
  // absolutely-positioned container itself scrolls with the content and the
  // browser cannot establish a sticky viewport relative to __scroll.
  const spacerTopStyle = useMemo<CSSProperties>(
    () => ({ height: range.offsetTop }),
    [range.offsetTop]
  );

  return (
    <div
      className={cn(
        'LeadsRegistryTable',
        isCompact && 'LeadsRegistryTable--compact'
      )}
    >
      <div
        ref={scrollContainerRef}
        className="LeadsRegistryTable__scroll"
        onScroll={scrollHandler}
      >
        <div className="LeadsRegistryTable__virtual-table" style={tableStyle}>
          <div className="LeadsRegistryTable__header" style={gridStyle}>
            <div className="LeadsRegistryTable__header-cell LeadsRegistryTable__selection-cell">
              <Checkbox
                checked={isAllCurrentRowsSelected}
                disabled={currentLeadIds.length === 0}
                indeterminate={isSomeCurrentRowsSelected}
                onChange={selectAllChangeHandler}
              />
            </div>

            {tableColumns.map((column, index) => (
              <div
                className="LeadsRegistryTable__header-cell"
                key={getColumnKey(column, index)}
              >
                {renderColumnTitle(column)}
              </div>
            ))}
          </div>

          {rows.length === 0 ? (
            <div className="LeadsRegistryTable__empty">Лиды не найдены</div>
          ) : (
            <div className="LeadsRegistryTable__body" style={spacerStyle}>
              {/* Static, compositor-painted row placeholders spanning the full
                  body height. They scroll with the content (no JS), so a fast
                  fling shows row-shaped skeleton bands instead of raw white
                  wherever the virtualized band has not caught up yet. Opaque
                  real rows paint over it the moment they render. */}
              <div
                className="LeadsRegistryTable__placeholder-layer"
                aria-hidden="true"
              />
              {/* Spacer pushes visible rows to offsetTop without position:absolute,
                  so position:sticky on cells inside works correctly. */}
              <div
                className="LeadsRegistryTable__visible-rows-spacer"
                style={spacerTopStyle}
                aria-hidden="true"
              />
              <div className="LeadsRegistryTable__visible-rows">
                {visibleRows.map((row, index) => {
                  const rowIndex = range.startIndex + index;

                  return (
                    <div
                      className={getRowClassName(row, highlightedLeadId)}
                      key={getRowKey(row)}
                      style={gridStyle}
                    >
                      <div className="LeadsRegistryTable__cell LeadsRegistryTable__selection-cell">
                        {row.kind === 'lead' ? (
                          <Checkbox
                            checked={selectedLeadIdsSet.has(row.id)}
                            onChange={({ target: { checked } }) =>
                              rowSelectionChangeHandler(row.id, checked)
                            }
                          />
                        ) : (
                          <Checkbox disabled />
                        )}
                      </div>

                      {tableColumns.map((column, columnIndex) => (
                        <div
                          className="LeadsRegistryTable__cell"
                          key={getColumnKey(column, columnIndex)}
                        >
                          {renderColumnCell(column, row, rowIndex)}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
